import React, { ChangeEvent, useEffect, useState } from 'react';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import constants from '../../../constants';
import CalibrationParam from '../../../Model/calibrationParam';
import SensorType from '../../../Model/sensorType';
import AddSensorRequest from '../../../Model/addSensorRequest';
import { SensorsImportResponse } from '../../../Model/SensorsImportResponse';
import { Container, InputLabel, TextField } from '@mui/material';
import AddSensorForm from '../../../Components/Sensors/AddSensor/AddSensorForm';
import ImportCsv from '../../../Components/Sensors/AddSensor/ImportCsv';
import {
  addSensors,
  getAlgorandAccounts,
  getSensorTypesOnlySensors,
  importSensors
} from '../../../api/sensors';
import { useTranslation } from 'react-i18next';
import CustomAlert, { CustomAlertType } from '../../../Components/CustomAlert';
import Loader from '../../../common-components/loader';

const CALIBRATION_PREFIX = 'calibration-';
const AddSensors = () => {
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorType, setErrorType] = useState<CustomAlertType>();
  const [errorMessage, setErrorMessage] = useState('');
  const [showSkippedDetails, setShowSkippedDetails] = useState(false);
  const [selectedSensorType, setSelectedSensorType] = useState('');
  const [selectedSensorTypeCode, setSelectedSensorTypeCode] = useState('');
  const [accounts, setAccounts] = useState<any[]>();
  const [sensorType, setSensorType] = useState<SensorType>();
  const [calibrationParams, setCalibrationParams] =
    useState<Record<string, unknown>>();
  const [ownerAccount, setOwnerAccount] = useState<string>();
  const [ownerMail, setOwnerMail] = useState<string>();
  const [infrastructureAccount, setInfrastructureAccount] = useState<string>();
  const [infrastructureMail, setInfrastructureMail] = useState<string>();
  const [sensorId, setSensorId] = useState<string>();
  const [infrastructurePercentage, setInfrastructurePercentage] =
    useState<number>();
  const [iccid, setIccid] = useState<string>();
  const [csvFile, setCsvFile] = useState<File>();
  const [csvString, setCsvString] = useState<string>();
  const [importOverView, setImportOverView] = useState<SensorsImportResponse>();
  const [importCSVResult, setImportCSVResult] = useState<string>();
  const [showSensorList, setShowSensorList] = useState<string>();
  const [selectedProductType, setSelectedProductType] = useState<string>();
  const [invalidCsvFile, setInvalidCsvFile] = useState<boolean>();
  const [sensorsTypes, setSensorsTypes] = useState<SensorType[]>([]);

  const { t } = useTranslation();

  let calibrations: { [key: string]: any; } = {};
  const defaultInfrastructurePercentage =
    constants.add_sensors.infrastructure_percentage;

  const getData = async () => {
    try {
      const result = await getAlgorandAccounts();
      if (result.data.success) {
        const accountsData = result.data.data.map((x: any) => {
          let name = x.mail;
          name += x.account ? ` - ${x.account}` : '';
          return {
            label: `${x.mail} - ${x.account}`,
            name,
            mail: x.mail,
            value: x.id,
            contact_id: x.contact_id,
            account: x.account
          };
        });
        setAccounts(accountsData);
      }
    } catch (err: any) {
      setLoading(false);
      setShowError(true);
      setErrorMessage(err.response.data.errorMessage);
      setErrorType(CustomAlertType.ALERT);
    }

    try {
      const result = await getSensorTypesOnlySensors();
      if (result.data.success) {
        setSensorsTypes(result.data?.data || []);
        Object.keys(constants.sensors_type).map((key: string) => {
          const sensorTypeData = (constants.sensors_type as any)[key];
          const code: string = sensorTypeData.value;
          calibrations[code] = sensorTypeData.calibration_params;
          return key;
        });
      }
    } catch (err: any) {
      setLoading(false);
      setShowError(true);
      setErrorMessage(err.response.data.errorMessage);
      setErrorType(CustomAlertType.ALERT);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const loadDefaultCalibrationParam = (param: CalibrationParam): unknown => {
    switch (param.type) {
      case 'string':
      case 'number': {
        return param.default;
      }
      case 'object': {
        const obj: Record<string, unknown> = {};
        param.children?.forEach((paramChild) => {
          obj[paramChild.name] = loadDefaultCalibrationParam(paramChild);
        });
        return obj;
      }
      default: {
        break;
      }
    }
    return undefined;
  };

  const loadDefaultCalibrationParams = (
    type: SensorType
  ): Record<string, unknown> => {
    const params: Record<string, unknown> = {};
    const defaultParams = type.calibration_params;
    defaultParams?.forEach((x) => {
      params[x.name] = loadDefaultCalibrationParam(x);
      console.log(x);
    });
    return params;
  };

  const typeSelected = (eventKey: any, name: string): void => {
    console.log(eventKey);
    setSelectedSensorType(name);
    setSelectedSensorTypeCode(eventKey);
    let type: SensorType | undefined = undefined;
    const sensorTypes: any = constants.sensors_type;
    for (let key of Object.keys(constants.sensors_type)) {
      if (sensorTypes[key].value === eventKey) {
        type = sensorTypes[key];
      }
    }
    if (type?.calibration_params && type?.calibration_params.length > 0) {
      setSensorType(type);
      setCalibrationParams(loadDefaultCalibrationParams(type));
    } else {
      setSensorType(type);
      setCalibrationParams(undefined);
    }
  };

  const calibrationChildrensParams = (params?: CalibrationParam[]) => {
    if (params && Array.isArray(params)) {
      return params.map((element: CalibrationParam) => (
        <>
          <InputLabel>{t(element.name.toLocaleUpperCase())}</InputLabel>
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          {calibrationParam(element)}
        </>
      ));
    }
    return null;
  };

  const onChangeCalibrationParam = (field: string, value: string) => {
    const nameComponents = field.split('-');
    const componentName = nameComponents[0];
    console.log(`Change value: ${field} -> ${componentName}`);
    const param = sensorType?.calibration_params?.find(
      (x) => x.name === componentName
    );
    switch (param?.type) {
      case 'string': {
        calibrationParams![param!.name] = value;
        break;
      }
      case 'number': {
        const num = Number.parseFloat(value);
        if (!Number.isNaN(+value) && !Number.isNaN(num)) {
          calibrationParams![param!.name] = num;
        } else {
          console.error(
            `Change calibration param value not correct number! ${field} -> ${value}`
          );
        }
        break;
      }
      case 'object': {
        // TODO: Recursive...
        break;
      }
      default:
        console.error(`Change calibration param value not handled: ${field}`);
    }
    const num = Number.parseFloat(value);
    if (!Number.isNaN(num)) {
      // data.latitude = num;
    }
  };

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const field = e.target.id;
    const { value } = e.target;

    switch (field) {
      case 'sensorId': {
        setSensorId(value);
        break;
      }
      case 'ownerMail': {
        setOwnerMail(value);
        break;
      }
      case 'infrastructureMail': {
        setInfrastructureMail(value);
        break;
      }
      case 'infrastructurePercentage':
        setInfrastructurePercentage(Number(parseFloat(value).toFixed(2)));
        break;
      case 'iccid': {
        setIccid(value);
        break;
      }
      default: {
        if (field.startsWith(CALIBRATION_PREFIX)) {
          const name = field.substr(CALIBRATION_PREFIX.length);
          {
            /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
          }
          onChangeCalibrationParam(name, value);
        } else {
          console.log(`Change value not handled: ${field}`);
        }
      }
    }
  };

  const calibrationParam = (
    element: CalibrationParam,
    parentPrefix?: string
  ) => {
    let component = null;
    switch (element.type) {
      case 'string': {
        component = (
          <TextField
            id={`${CALIBRATION_PREFIX}${parentPrefix ?? ''}${element.name}`}
            fullWidth
            size="small"
            sx={{
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 }
            }}
            required
            type="text"
            placeholder={t(element.name.toLocaleUpperCase())}
            defaultValue={element.default}
            onChange={onChangeValue}
          />
        );
        break;
      }
      case 'number': {
        // eslint-disable-next-line i18next/no-literal-string
        component = (
          <TextField
            fullWidth
            size="small"
            sx={{
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 }
            }}
            required
            className="hide-stepper"
            type="number"
            placeholder={t(element.name.toLocaleUpperCase())}
            defaultValue={element.default}
            onChange={onChangeValue}
          />
        );
        break;
      }
      case 'object': {
        component = (
          <div className="calibration-param-group" key={element.name}>
            {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
            {calibrationChildrensParams(element.children)}
          </div>
        );
        break;
      }
      default: {
        break;
      }
    }
    return component;
  };

  const onCalibrationParams = () => {
    if (
      sensorType?.calibration_params &&
      Array.isArray(sensorType?.calibration_params)
    ) {
      return (
        <>
          <InputLabel className="inputLabel">
            {t('CALIBRATION_PARAMS')}
          </InputLabel>
          <div className="calibration-param-group">
            {calibrationChildrensParams(sensorType?.calibration_params)}
          </div>
        </>
      );
    }
    return null;
  };

  const handleSubmit = async () => {
    const submitData: AddSensorRequest = {
      sensorId: sensorId!,
      type: sensorType?.value ? sensorType!.value : selectedSensorTypeCode,
      ownerAccount: ownerAccount!,
      ownerMail: ownerMail!,
      infrastructureAccount: infrastructureAccount,
      infrastructureMail: infrastructureMail,
      iccid: iccid,
      infrastructure_percentage: infrastructurePercentage
    };
    if (calibrationParams) {
      submitData.calibrationParams = JSON.stringify(calibrationParams!);
    }
    setLoading(true);
    try {
      const result = await addSensors(submitData);
      setLoading(false);
      if (result.data.success) {
        setShowError(true);
        setErrorMessage(`Sensore aggiunto, id: ${result.data.data.id}`);
        setErrorType(CustomAlertType.INFO);
      }
    } catch (reason: any) {
      setLoading(false);
      setShowError(true);
      setErrorMessage(reason.response.data.errorMessage);
      setErrorType(CustomAlertType.ALERT);
    }
  };

  const validateKaiterraSensorId = () => {
    let sensorIdValue = (sensorId || '').replace(/-/gm, '');
    const prefix = sensorIdValue.startsWith('KAI_') ? '' : 'KAI_';
    setSensorId(`${prefix}${sensorId}`);
  };

  const importCSV = async () => {
    const formData = new FormData();
    formData.append('sensors', csvFile as Blob);
    try {
      setLoading(true);
      const result = await importSensors(formData);
      setImportOverView(result.data?.data);
      setShowSkippedDetails(false);
      setImportCSVResult(undefined);
      setLoading(false);
    } catch (error: any) {
      let message = error.message;
      if (error.response?.data) {
        message = error.response?.data?.errorMessage || error.message;
      }
      setImportOverView(undefined);
      setShowSkippedDetails(false);
      setImportCSVResult(message);
      setLoading(false);
    }
  };

  const showSensorsList = (key: 'inserted' | 'alreadyInserted' | 'updated') => {
    const sensorListKeyValue = key === showSensorList ? undefined : key;
    setShowSensorList(sensorListKeyValue);
  };

  const validateCSVFile = (csvStringValue?: string) => {
    const dataString = csvStringValue || csvString;
    if (dataString) {
      const dataRows = dataString.split('\n');
      // Take the header row (by default is the first row in the CSV file)
      const header = dataRows
        .shift()
        ?.split(';')
        .map((h) => h.trim());
      const productTypeIndex = header?.findIndex(
        (h) => (h || '').trim() === 'Sensor Type'
      );
      const isInvalid =
        productTypeIndex !== undefined
          ? dataRows
            .filter((dr) => dr !== '')
            .map((dr) => dr.split(';'))
            .find((dr) => {
              const bool =
                dr[productTypeIndex] !== selectedProductType &&
                (dr[productTypeIndex] !== undefined ||
                  dr[productTypeIndex] !== '');
              return bool;
            })
          : true;
      if (isInvalid) {
        setImportCSVResult(t('CSV_CONTAINS_OTHER_TYPES'));
        setInvalidCsvFile(true);
      } else {
        setImportCSVResult(undefined);
        setInvalidCsvFile(false);
      }
    }
  };
  useEffect(() => {
    validateCSVFile();
  }, [selectedProductType]);

  return (
    <Container>
      <Loader visible={loading} fullscreen={true} />

      <AddSensorForm
        sensorType={sensorType}
        selectedSensorType={selectedSensorType}
        validateKaiterraSensorId={validateKaiterraSensorId}
        onChangeValue={(e: ChangeEvent<HTMLInputElement>) => onChangeValue(e)}
        ownerAccount={ownerAccount}
        infrastructureAccount={infrastructureAccount}
        sensorId={sensorId}
        accounts={accounts}
        infrastructureMail={infrastructureMail}
        sensorsTypes={sensorsTypes}
        typeSelected={(code: string, name: string) => typeSelected(code, name)}
        setOwnerMail={(e: string) => setOwnerMail(e)}
        setInfrastructurePercentage={(e: number) =>
          setInfrastructurePercentage(e)
        }
        setInfrastructureAccount={(e: string) => setInfrastructureAccount(e)}
        setOwnerAccount={(e: string) => setOwnerAccount(e)}
        infrastructurePercentage={infrastructurePercentage}
        defaultInfrastructurePercentage={defaultInfrastructurePercentage}
        handleSubmit={handleSubmit}
        onCalibrationParams={onCalibrationParams}
        ownerMail={ownerMail}
        setInfrastructureMail={(e: string) => setInfrastructureMail(e)}
      ></AddSensorForm>

      <ImportCsv
        importCSV={importCSV}
        setCsvString={setCsvString}
        validateCSVFile={validateCSVFile}
        setCsvFile={setCsvFile}
        setSelectedProductType={setSelectedProductType}
        sensorsTypes={sensorsTypes}
        importCSVResult={importCSVResult}
        importOverView={importOverView}
        showSensorsList={showSensorsList}
        showSensorList={showSensorList}
        setShowSkippedDetails={setShowSkippedDetails}
        showSkippedDetails={showSkippedDetails}
        selectedProductType={selectedProductType}
        csvFile={csvFile}
        invalidCsvFile={invalidCsvFile}
      ></ImportCsv>

      <CustomAlert
        show={showError}
        message={errorMessage}
        type={errorType}
        onClose={() => {
          setShowError(false);
          setErrorMessage('');
        }}
      />
    </Container>
  );
};

export default AddSensors;
